import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, User, Shield, Building, Settings } from 'lucide-react';
import { ApiClient } from '../../api/client.js';
import { SearchResult } from '../../types/index.js';
import './search.css';

interface UniversalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UniversalSearchModal: React.FC<UniversalSearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        isOpen ? onClose() : undefined;
      }
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await ApiClient.get(`/search?q=${encodeURIComponent(query)}`);
      if (res.success && res.results) {
        setResults(res.results);
      }
      setLoading(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  if (!isOpen) return null;

  const handleSelect = (url: string) => {
    onClose();
    navigate(url);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Users': return User;
      case 'Roles & Permissions': return Shield;
      case 'Departments': return Building;
      default: return Settings;
    }
  };

  return (
    <div className="search-backdrop" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="search-header">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search users, roles, departments, system modules... (Press Esc to close)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <button className="search-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="search-body">
          {loading && <div className="search-loading">Searching Drishti Business OS...</div>}

          {!loading && query && results.length === 0 && (
            <div className="search-empty">No matching records found for "{query}"</div>
          )}

          {!loading && results.length > 0 && (
            <div className="search-results-list">
              {results.map((res) => {
                const Icon = getCategoryIcon(res.category);
                return (
                  <div key={res.id} className="search-result-item" onClick={() => handleSelect(res.url)}>
                    <div className="result-icon-box">
                      <Icon size={16} />
                    </div>
                    <div className="result-info">
                      <span className="result-title">{res.title}</span>
                      <span className="result-subtitle">{res.subtitle}</span>
                    </div>
                    <span className="result-category">{res.category}</span>
                    <ArrowRight size={14} className="result-arrow" />
                  </div>
                );
              })}
            </div>
          )}

          {!query && (
            <div className="search-suggestions">
              <span className="suggestion-title">QUICK SYSTEM NAVIGATIONS</span>
              <div className="suggestion-tags">
                <span onClick={() => handleSelect('/users')}>Users Directory</span>
                <span onClick={() => handleSelect('/roles')}>Access Matrix</span>
                <span onClick={() => handleSelect('/departments')}>Departments</span>
                <span onClick={() => handleSelect('/audit')}>Security Logs</span>
                <span onClick={() => handleSelect('/settings')}>Company Branding</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
